import type { FullContext, InvokePayload } from '@forge/bridge/out/types'
import type { History } from 'history'
import type { ReactNode } from 'react'
import type { z } from 'zod'
import type {
  AllRadarMetadataSchema,
  ChartSchema,
  FlagConfigSchema,
  ItemReleaseSchema,
  ItemSchema,
  QuadrantSchema,
  RadarConfigSchema,
  RadarDataSchema,
  RadarDataStorageSchema,
  RadarMetadataSchema,
  RingSchema,
  UserSettings
} from './src/schema/zodSchemas'

export interface State {
  context: FullContext | null
  env: Env | null
  radarConfig: RadarConfig | null
  radarItems: Item[]
  isLoading: boolean
  isAdmin: boolean
  isLocal: boolean | null
  error: Error | null
  availableRadars: RadarMetadata[] | null
  currentRadarId: number | null
  embedConfig: null | { apiUrl: string; radarId: string }
  userSettings: UserSettings | undefined
  userEmail?: string | null
  history: History | null
}

export enum Flag {
  New = 'new',
  Changed = 'changed',
  Default = 'default'
}

export type ItemRelease = z.infer<typeof ItemReleaseSchema>
export type Item = z.infer<typeof ItemSchema>
export type RadarData = z.infer<typeof RadarDataSchema>
export type Ring = z.infer<typeof RingSchema>
export type Quadrant = z.infer<typeof QuadrantSchema>
export type FlagConfig = z.infer<typeof FlagConfigSchema>
export type ChartConfig = z.infer<typeof ChartSchema>
export type RadarConfig = z.infer<typeof RadarConfigSchema>
export type RadarMetadata = z.infer<typeof RadarMetadataSchema>
export type AllRadarMetadata = z.infer<typeof AllRadarMetadataSchema>

export type Env = {
  posthog: {
    api_host: string
    api_key: string
  }
  stripe: {
    perishableKey: string
  }
  userEmail?: string | null
}

export interface ChartProps {
  size?: number
  quadrants: readonly Quadrant[]
  rings: readonly Ring[]
  items: readonly Item[]
  className?: string
}

export interface LicenseCheckResponse {
  maxItems: number
  canAdd: boolean
  currentCount: number
}

// --- Consolidated from compassPage/src/lib/types.ts and src/types.ts ---

// Billing & Subscription Types
export type BillingInterval = 'month' | 'year'
export type SubscriptionTier = 'BASIC' | 'PRO'

export interface LicenseStatus {
  isActive: boolean
  tier: SubscriptionTier | null
  siteUrl: string
  lastChecked: number
}

export type RequestContext = {
  cloudId: string
  moduleKey: string
  siteUrl: string
  localId?: string
  environmentId?: string
  environmentType?: string
  extension?: {
    type: string
    url: string
    renderingLocation: string
  }
  installContext?: string
  accountId?: string
  license?: {
    isActive: boolean
    billingPeriod: string | null
    capabilitySet: string | null
    ccpEntitlementId: string | null
    ccpEntitlementSlug: string | null
    isEvaluation: boolean | null
    subscriptionEndDate: string | null
    supportEntitlementNumber: string | null
    trialEndDate: string | null
    type: string | null
    modes: string[] | null
  }
  jobId?: string
}

// RadarDataStorage for backend
export type RadarDataStorage = z.infer<typeof RadarDataStorageSchema>

// --- SETTINGS PAGE TYPES ---

export interface SettingsSectionProps {
  title: string
  children: ReactNode
}

// Result type for deleteRadar resolver
export type DeleteRadarResult = {
  success: boolean
  error?: string
}

export interface SettingsCardProps {
  children: ReactNode
}

export type { InvokePayload }
